/**
 * Section builders for Static Web Generator
 * Handles complex transformations for array/object sections
 */

import { WebsiteGeneratorState } from "../../../types/staticWebGenerator";
import { getPhotoUrl } from "./photoUtils";
import { hasValue, getValue, filterEmptyItems } from "./valueUtils";

/**
 * Build gallery items array for API request
 */
export const buildGalleryItems = (
  state: WebsiteGeneratorState,
): any[] | undefined => {
  if (state.gallery === null) return undefined;

  // Include items that are NOT explicitly deselected (selected !== false)
  const selectedItems =
    state.gallery.items?.filter((item) => item.selected !== false) || [];

  const galleryItems = selectedItems
    .map((item) => {
      const imageUrl = getPhotoUrl(item);
      if (!imageUrl) return null;

      const galleryItem: any = { image_url: imageUrl };
      const title = getValue((item as any).title || item.filename);
      if (title) galleryItem.title = title;
      const description = getValue((item as any).description);
      if (description) galleryItem.description = description;
      return galleryItem;
    })
    .filter((item) => item !== null);

  return galleryItems;
};

/**
 * Build testimonials array for API request
 */
export const buildTestimonialsArray = (
  state: WebsiteGeneratorState,
): any[] | undefined => {
  if (state.testimonials === null) return undefined;

  const cards = state.testimonials.cards || [];
  const testimonialCards = cards
    .filter((card) => hasValue(card.name) && hasValue(card.text))
    .map((card) => {
      const testimonial: any = {
        name: getValue(card.name),
        content: getValue(card.text),
      };
      const picture = getPhotoUrl(card.image);
      if (picture) testimonial.picture = picture;
      const source = getValue(card.title);
      if (source) testimonial.source = source;
      return testimonial;
    });

  return testimonialCards;
};

/**
 * Build service highlights array for API request
 */
export const buildHighlightsArray = (
  state: WebsiteGeneratorState,
): any[] | undefined => {
  if (state.highlights === null) return undefined;

  const items = state.highlights.items || [];
  const highlightItems = items
    .filter((item) => hasValue(item.title) || hasValue(item.body))
    .map((item) => {
      const highlight: any = {};
      if (hasValue(item.icon)) highlight.icon = getValue(item.icon);
      if (hasValue(item.title)) highlight.title = getValue(item.title);
      if (hasValue(item.body)) highlight.body = getValue(item.body);
      if (item.id !== undefined) highlight.id = item.id;
      return highlight;
    });

  return highlightItems;
};

/**
 * Build announcement modal items array for API request
 */
export const buildAnnouncementItems = (
  state: WebsiteGeneratorState,
): string[] | undefined => {
  const rawValue = state.announcementBanner?.items;
  if (rawValue === null || rawValue === undefined) return undefined;
  return filterEmptyItems(rawValue);
};

/**
 * Get the appropriate array builder for a section/field
 */
export const getArrayBuilder = (
  schemaSection: string,
  schemaField: string,
): ((state: WebsiteGeneratorState) => any[] | undefined) | null => {
  // Gallery items
  if (
    schemaSection === "gallery" &&
    (schemaField === "items" ||
      schemaField === "images" ||
      schemaField === "photos")
  ) {
    return buildGalleryItems;
  }

  // Testimonials
  if (
    schemaSection === "testimonials" &&
    (schemaField === "testimonials" ||
      schemaField === "items" ||
      schemaField === "cards")
  ) {
    return buildTestimonialsArray;
  }

  // Service highlights
  if (schemaSection === "service_highlights" && schemaField === "highlights") {
    return buildHighlightsArray;
  }

  // Announcement modal items
  if (schemaSection === "announcement_modal" && schemaField === "items") {
    return buildAnnouncementItems;
  }

  return null;
};
